import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import { Box } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { api, unwrap } from '../../../api/client';
import { useAuth } from '../../../auth/AuthProvider';
import { Money } from '../../../components/Money';
import { useNotify } from '../../../lib/notify';
import { DetailHeader } from '../components/DocParts';
import { DocumentEditor } from '../components/DocumentEditor';
import { afterSaleKeys, idem, type SalesInvoice } from '../lib/api';
import { cartFromDocument, copyHeader } from '../lib/documents';
import { useCopy } from '../../../lib/useCopy';
import { toInvoiceRequest } from '../lib/requests';

export function InvoiceNewPage() {
  const { me } = useAuth();
  const navigate = useNavigate();
  const notify = useNotify();
  const qc = useQueryClient();
  // Duplicate: the copied invoice's customer, lines and prices as a new invoice dated today.
  const copy = useCopy() as SalesInvoice | null;
  return (
    <Box>
      <DetailHeader
        back={{ to: '/sales/invoices', label: 'Invoices' }}
        title={copy ? `New invoice · copy of ${copy.number}` : 'New invoice'}
        subtitle="Bill a customer on account. Stock moves when you record a delivery."
      />
      <DocumentEditor
        key={copy?.id ?? 'new'}
        kind="invoice"
        {...(copy
          ? { initialCart: cartFromDocument(copy), initialHeader: copyHeader(copy) }
          : { draftKey: `sm.draft.invoice.${me?.tenant.id ?? ''}` })}
        actions={[
          {
            label: 'Post invoice',
            primary: true,
            icon: <ReceiptLongRounded />,
            confirm: (v, total) => ({
              title: 'Post this invoice?',
              body: (
                <>
                  This adds <Money value={total} /> to {v.header.contact.name}’s balance and records
                  the sale in the accounts. A posted invoice can only be cancelled, not edited.
                </>
              ),
            }),
            run: async (v) => {
              const inv = await unwrap(
                api.POST('/sales-invoices', {
                  params: { header: idem() },
                  body: toInvoiceRequest(v, crypto.randomUUID()),
                }),
              );
              notify.success(`Invoice ${inv.number} posted`);
              await Promise.all(
                afterSaleKeys.map((k) => qc.invalidateQueries({ queryKey: [...k] })),
              );
              void navigate(`/sales/invoices/${inv.id}`, { replace: true });
            },
          },
        ]}
      />
    </Box>
  );
}
